import { SimpleLayout } from '../../layouts/simple'
import { Teacher } from '../../providers/context'
import { Button } from '../../components/simple/button'
import { LocalTable } from '../../components/table'
import React from 'react'
import { useRequest } from '../../request'
import { ColumnDef } from '@tanstack/react-table'
import { TextInput } from '../../components/form/textInput'
import { Divider } from '../../components/simple/divider'
import { Histogram } from '../../components/charts/barChart'
import { Loader } from '../../components/simple/loader'
import { IconCaretLeft, IconCaretRight } from '../../icons/library'
import { Tooltip } from '../../components/simple/tooltip'

type StudentInviteRow = {
  name: string
  syncID: number
  classesIncluded: string[]
  classesExcludedNum: number
}
type TeacherInviteRow = {
  name: string
  students: number
  classesIncluded: string[]
  classesExcludedNum: number
}

const studentColumns: ColumnDef<StudentInviteRow>[] = [
  {
    header: 'Name',
    accessorKey: 'name',
  },
  {
    header: 'Sync ID',
    accessorKey: 'syncID',
  },
  {
    header: '# of Classes Included (Invites)',
    accessorFn: (row) => row.classesIncluded.length,
    cell: ({ row }) => (
      <Tooltip text={row.original.classesIncluded.join(', ')}>
        <div>{row.original.classesIncluded.length}</div>
      </Tooltip>
    ),
  },
  {
    header: '# of Classes Excluded',
    accessorKey: 'classesExcludedNum',
  },
]

const teacherColumns: ColumnDef<TeacherInviteRow>[] = [
  {
    header: 'Name',
    accessorKey: 'name',
  },
  {
    header: '# of Students',
    accessorKey: 'students',
  },
  {
    header: '# of Classes Included (Invites)',
    accessorFn: (row) => row.classesIncluded.length,
    cell: ({ row }) => (
      <Tooltip text={row.original.classesIncluded.join(', ')}>
        <div>{row.original.classesIncluded.length}</div>
      </Tooltip>
    ),
  },
  {
    header: '# of Classes Excluded',
    accessorKey: 'classesExcludedNum',
  },
]

export const VerifyInvitesPage: React.FC<{ user: Teacher; id: number }> = ({ user, id }) => {
  const { data } = useRequest<{ name: string; students: StudentInviteRow[]; teachers: TeacherInviteRow[] }>(
    '/frontend_api/getConferenceInviteVerification',
    React.useMemo(
      () => ({
        conferenceID: id,
      }),
      [id]
    )
  )

  const [studentSearch, setStudentSearch] = React.useState('')
  const [teacherSearch, setTeacherSearch] = React.useState('')

  const inviteDistribution = React.useMemo(() => {
    if (!data) return undefined
    const invites = data.students.map((s) => s.classesIncluded.length)
    return invites.reduce(
      (acc, val) => {
        acc[val] = (acc[val] || 0) + 1
        return acc
      },
      {} as Record<number, number>
    )
  }, [data])

  return (
    <SimpleLayout>
      {data ? (
        <div className={`my-4 flex flex-col gap-2 self-center`}>
          <div className="mt-2 self-center text-xl font-bold">Verify invites for conference "{data.name}"</div>
          <div className="mt-4 flex items-center justify-between gap-2">
            <div className="text-lg font-bold">Potential invite distribution</div>{' '}
            <div className="text-sm">
              This histogram shows how many students will receive a each number of invites, when invited.
            </div>
          </div>
          {inviteDistribution && <Histogram data={inviteDistribution} x={'Potential Invites'} y={'Students'} />}
          <Divider />
          <div className="mt-4 flex items-center justify-between gap-2">
            <div className="text-lg font-bold">Potential invites by student</div>
            <TextInput value={studentSearch} onChange={setStudentSearch} placeholder="Search..." />
          </div>
          <LocalTable
            sortable
            pagination={10}
            columns={studentColumns}
            data={data.students}
            exportName="Potential invites by student"
            search={studentSearch}
          />
          <Divider />
          <div className="mt-4 flex items-center justify-between gap-2">
            <div className="text-lg font-bold">Potential invites by teacher</div>
            <TextInput value={teacherSearch} onChange={setTeacherSearch} placeholder="Search..." />
          </div>
          <LocalTable
            sortable
            pagination={10}
            columns={teacherColumns}
            data={data.teachers}
            exportName="Potential invites by teacher"
            search={teacherSearch}
          />
          <Divider />
          <div className="mt-4 flex items-center justify-between gap-8">
            <Button link={`/ptc/adminConferenceVerification/exclusionClasses/${id}`}>
              <IconCaretLeft />
              Back to Exclusion Classes
            </Button>
            <Button logo link={`/ptc/adminConferenceVerification/teacherInfo/${id}`}>
              Review Teacher Locations <IconCaretRight />
            </Button>
          </div>
        </div>
      ) : (
        <Loader className="mt-8" />
      )}
    </SimpleLayout>
  )
}
